/**
 * The Prepared by box of a form with one signer and no reviewer.
 *
 * The 203, 205A, 215 and 234 all print the same four blanks, so they share this
 * rather than each keeping a copy of it. Signing itself happens on the version
 * bar above, as on every versioned form; this box only shows the result.
 */

import type React from 'react';
import { useMemo } from 'react';

import { useFormVersionTip } from 'components/IAP/hooks/icsFormVersionHooks';
import { Ics204CardSection } from './AssignmentList/ics204BoxLabels';
import {
  formatSignatureDateTime,
  parseWalaSignatures,
  type WalaSignature,
} from './AssignmentList/ics204SignatureSlots';

export type IcsPreparedBySectionProps = {
  /** The period's signature, or undefined while the form is unsigned. */
  signature?: WalaSignature;
  /** The printed box label, numbered as that form's sheet numbers it. */
  boxLabel: string;
  testId: string;
  /** Optional product-tour anchor (`data-tour`). */
  tour?: string;
  /** Shown in place of the blanks until someone signs. */
  unsignedMessage?: string;
};

export type IcsPreparedByBoxProps = Omit<
  IcsPreparedBySectionProps,
  'signature'
> & {
  baseId: string;
  periodNumber: number;
};

const Line: React.FC<{ label: string; value: string; italic?: boolean }> = ({
  label,
  value,
  italic,
}) => (
  <div className={italic ? 'tw:italic' : undefined}>
    <span
      className={
        italic
          ? 'tw:not-italic tw:text-muted-foreground'
          : 'tw:text-muted-foreground'
      }
    >
      {label}:{' '}
    </span>
    {value}
  </div>
);

/**
 * The box itself, given a signature. Forms that keep their versions outside the
 * shared `ics_form_version` table — the 215 — hold their own signature already
 * and use this directly.
 */
export const IcsPreparedBySection: React.FC<IcsPreparedBySectionProps> = ({
  signature,
  boxLabel,
  testId,
  tour,
  unsignedMessage = 'Not signed. Create a signed version above to sign this form.',
}) => (
  <Ics204CardSection
    boxLabel={boxLabel}
    testId={testId}
    tour={tour}
    className="tw:mt-0"
  >
    {signature ? (
      <div className="tw:grid tw:gap-1 tw:text-sm">
        <Line label="Name" value={signature.name} />
        <Line
          label="Position / Title"
          // Either typed by the signer or the roster seat resolved by the
          // server, depending on the form. Signatures recorded before it was
          // captured fall back to the slot they filled.
          value={signature.positionTitle || signature.role}
        />
        <Line label="Signature" value={signature.name} italic />
        <Line
          label="Date / Time"
          value={formatSignatureDateTime(signature.signedAt)}
        />
      </div>
    ) : (
      <div className="tw:text-sm tw:text-muted-foreground">
        {unsignedMessage}
      </div>
    )}
  </Ics204CardSection>
);

/** The box wired to a form's tip in the shared version table. */
export const IcsPreparedByBox: React.FC<IcsPreparedByBoxProps> = ({
  baseId,
  periodNumber,
  ...sectionProps
}) => {
  const { data: tip } = useFormVersionTip(baseId, periodNumber);
  const signature = useMemo(
    () => parseWalaSignatures(tip?.signatures)[0],
    [tip?.signatures]
  );

  return <IcsPreparedBySection signature={signature} {...sectionProps} />;
};

export default IcsPreparedByBox;
